// ---------------------------------------------------------------------------
// Interactive ER canvas — draggable/zoomable entity-relationship diagram
// (Phase 2 market-readiness item: a real alternative to the static Mermaid
// ER diagram, closer to what erwin/ER-Studio/Hackolade/SqlDBM offer).
// Functional, not just visual: click a table to edit it (reuses the same
// entity editor as the Overview tab), click a linkage to change its
// cardinality or delete it, filter which tables are shown (with search),
// toggle linkage visibility on/off, switch between grid/star layout, and
// drag from one column to another to propose a new relationship.
// ---------------------------------------------------------------------------
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  applyNodeChanges,
  type Node,
  type Edge,
  type NodeProps,
  type Connection,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  KeyRound,
  Link2,
  Filter,
  ChevronDown,
  Eye,
  EyeOff,
  X,
  Check,
  Loader2,
  Trash2,
  Search,
  LayoutGrid,
  Sparkles,
} from "lucide-react";

interface CanvasColumn {
  technical_name: string;
  business_name?: string;
  data_type?: string;
}

interface CanvasEntity {
  name: string;
  entity_type?: string;
  columns?: CanvasColumn[];
  primary_keys?: string[];
  foreign_keys?: Array<{ column?: string; source_column?: string }>;
}

interface CanvasRelationship {
  from_entity?: string;
  to_entity?: string;
  source_entity?: string;
  target_entity?: string;
  from_column?: string;
  to_column?: string;
  source_column?: string;
  target_column?: string;
  cardinality?: string;
  confidence?: number;
}

const MAX_VISIBLE_COLUMNS = 8;
const NODE_WIDTH = 220;
const NODE_H_SPACING = 280;
const NODE_V_SPACING = 240;
const ENTITY_HANDLE_ID = "__entity__";

const CARDINALITY_LABELS: Record<string, string> = {
  one_to_one: "1:1",
  one_to_many: "1:N",
  many_to_one: "N:1",
  many_to_many: "N:N",
};

/** Backend relationship records vary in field naming (from_entity/to_entity
 * vs source_entity/target_entity) depending on which engine produced them —
 * accept either shape here, same as the parent view does. */
function relFields(r: CanvasRelationship) {
  return {
    from: r.from_entity || r.source_entity || "",
    to: r.to_entity || r.target_entity || "",
    fromCol: r.from_column || r.source_column || "",
    toCol: r.to_column || r.target_column || "",
    cardinality: r.cardinality || "many_to_one",
    confidence: typeof r.confidence === "number" ? r.confidence : undefined,
  };
}

function columnHandleId(columnName: string) {
  return `col:${columnName}`;
}

function EntityNode({ data }: NodeProps) {
  const entity = data.entity as CanvasEntity;
  const columns = entity.columns ?? [];
  const pkSet = new Set(entity.primary_keys ?? []);
  const fkSet = new Set(
    (entity.foreign_keys ?? [])
      .map((fk) => fk.column ?? fk.source_column)
      .filter(Boolean) as string[],
  );
  const visible = columns.slice(0, MAX_VISIBLE_COLUMNS);
  const overflow = columns.length - visible.length;

  return (
    <div
      style={{ width: NODE_WIDTH }}
      title="Click to edit this table · drag from a column's dot to another column to propose a relationship"
      className="rounded-lg border border-brand-500/30 bg-surface-100 shadow-lg overflow-hidden text-left cursor-pointer hover:border-brand-400/60 hover:shadow-brand-500/10 transition-colors"
    >
      <Handle
        type="target"
        id={ENTITY_HANDLE_ID}
        position={Position.Top}
        isConnectable={false}
        className="!bg-brand-500"
      />
      <Handle
        type="source"
        id={ENTITY_HANDLE_ID}
        position={Position.Bottom}
        isConnectable={false}
        className="!bg-brand-500"
      />
      <div className="px-3 py-2 bg-brand-500/15 border-b border-brand-500/20">
        <p className="text-xs font-semibold text-white truncate">
          {entity.name}
        </p>
        {entity.entity_type && (
          <p className="text-[10px] text-white/40 uppercase tracking-wide">
            {entity.entity_type}
          </p>
        )}
      </div>
      <ul className="divide-y divide-white/5">
        {visible.map((col) => (
          <li
            key={col.technical_name}
            className="relative group px-3 py-1 flex items-center gap-1.5 text-[11px] text-white/70"
          >
            <Handle
              type="target"
              id={columnHandleId(col.technical_name)}
              position={Position.Left}
              className="!w-2 !h-2 !border-0 !bg-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity"
            />
            {pkSet.has(col.technical_name) ? (
              <KeyRound size={10} className="text-amber-400 shrink-0" />
            ) : fkSet.has(col.technical_name) ? (
              <Link2 size={10} className="text-cyan-400 shrink-0" />
            ) : (
              <span className="w-[10px] shrink-0" />
            )}
            <span className="truncate">{col.technical_name}</span>
            <Handle
              type="source"
              id={columnHandleId(col.technical_name)}
              position={Position.Right}
              className="!w-2 !h-2 !border-0 !bg-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </li>
        ))}
        {overflow > 0 && (
          <li className="px-3 py-1 text-[10px] text-white/30 italic">
            +{overflow} more
          </li>
        )}
      </ul>
    </div>
  );
}

const nodeTypes = { entity: EntityNode };

function gridLayoutNodes(entities: CanvasEntity[]): Node[] {
  const cols = Math.max(1, Math.ceil(Math.sqrt(entities.length)));
  return entities.map((entity, i) => ({
    id: entity.name,
    type: "entity",
    position: {
      x: (i % cols) * NODE_H_SPACING,
      y: Math.floor(i / cols) * NODE_V_SPACING,
    },
    data: { entity },
  }));
}

/** Star/radial layout: entities typed "Fact" (or, absent any Fact entities,
 * the single entity with the most relationship connections) are placed in
 * a center column; everything else is arranged in a ring around them. Mimics
 * the classic "star schema" diagram shape modelers expect for fact/dimension
 * layouts, rather than the plain grid. */
function starLayoutNodes(
  entities: CanvasEntity[],
  relationships: CanvasRelationship[],
): Node[] {
  if (entities.length <= 2) return gridLayoutNodes(entities);

  const degree = new Map<string, number>();
  for (const rel of relationships) {
    const f = relFields(rel);
    if (f.from) degree.set(f.from, (degree.get(f.from) ?? 0) + 1);
    if (f.to) degree.set(f.to, (degree.get(f.to) ?? 0) + 1);
  }

  let centers = entities.filter(
    (e) => (e.entity_type || "").toLowerCase() === "fact",
  );
  if (centers.length === 0) {
    const ranked = [...entities].sort(
      (a, b) => (degree.get(b.name) ?? 0) - (degree.get(a.name) ?? 0),
    );
    centers = ranked.slice(0, 1);
  }
  const centerNames = new Set(centers.map((c) => c.name));
  const satellites = entities.filter((e) => !centerNames.has(e.name));

  const nodes: Node[] = centers.map((entity, i) => ({
    id: entity.name,
    type: "entity",
    position: {
      x: 0,
      y: i * NODE_V_SPACING - ((centers.length - 1) * NODE_V_SPACING) / 2,
    },
    data: { entity },
  }));

  const radiusX = NODE_H_SPACING * 1.6;
  const radiusY = NODE_V_SPACING * Math.max(1, satellites.length / 6);
  satellites.forEach((entity, i) => {
    const angle = (2 * Math.PI * i) / Math.max(1, satellites.length);
    nodes.push({
      id: entity.name,
      type: "entity",
      position: {
        x: Math.cos(angle) * radiusX,
        y: Math.sin(angle) * radiusY,
      },
      data: { entity },
    });
  });

  return nodes;
}

/** Builds edges from (relationship, original-array-index) pairs so click
 * handlers can report back which entry in the full relationships array was
 * clicked, even though `pairs` may be a filtered subset. */
function buildEdges(
  pairs: Array<{ rel: CanvasRelationship; index: number }>,
): Edge[] {
  return pairs.map(({ rel, index }) => {
    const f = relFields(rel);
    const cardinalityLabel = CARDINALITY_LABELS[f.cardinality] ?? f.cardinality;
    const label =
      typeof f.confidence === "number"
        ? `${cardinalityLabel} · ${Math.round(f.confidence * 100)}%`
        : cardinalityLabel;
    return {
      id: `rel-${index}`,
      source: f.from,
      target: f.to,
      sourceHandle: f.fromCol ? columnHandleId(f.fromCol) : ENTITY_HANDLE_ID,
      targetHandle: f.toCol ? columnHandleId(f.toCol) : ENTITY_HANDLE_ID,
      label,
      style: { stroke: "rgba(134,188,37,0.5)" },
      labelStyle: { fill: "rgba(255,255,255,0.6)", fontSize: 10 },
      data: { index },
    };
  });
}

export function SemanticERCanvas({
  entities,
  relationships,
  onEditEntity,
  onUpdateRelationshipCardinality,
  onDeleteRelationship,
  onCreateRelationship,
}: {
  entities: CanvasEntity[];
  relationships: CanvasRelationship[];
  /** Called when a table node is clicked. The parent owns the actual edit
   * UI (it reuses the existing entity editor) — this component is purely
   * a trigger. */
  onEditEntity?: (entityName: string) => void;
  /** Called when the user saves a new cardinality for a linkage. Index is
   * the position of the relationship within the full `relationships` prop
   * array (not the filtered/visible subset). Should reject on failure. */
  onUpdateRelationshipCardinality?: (
    index: number,
    cardinality: string,
  ) => Promise<void>;
  /** Called when the user deletes a linkage. Same indexing as above. */
  onDeleteRelationship?: (index: number) => Promise<void>;
  /** Called when the user drags from one column's connection dot to
   * another, proposing a new relationship between those two columns. */
  onCreateRelationship?: (
    fromEntity: string,
    fromColumn: string,
    toEntity: string,
    toColumn: string,
  ) => Promise<void>;
}) {
  // null = show all tables; otherwise the explicit set of visible table names
  const [selectedTables, setSelectedTables] = useState<Set<string> | null>(
    null,
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [tableSearch, setTableSearch] = useState("");
  const [showRelationships, setShowRelationships] = useState(true);
  const [layoutMode, setLayoutMode] = useState<"grid" | "star">("grid");
  const [selectedEdgeIndex, setSelectedEdgeIndex] = useState<number | null>(
    null,
  );
  const [edgeCardinalityDraft, setEdgeCardinalityDraft] =
    useState("many_to_one");
  const [edgeBusy, setEdgeBusy] = useState(false);
  const [edgeError, setEdgeError] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);

  const isTableVisible = useCallback(
    (name: string) => selectedTables === null || selectedTables.has(name),
    [selectedTables],
  );

  const toggleTable = (name: string) => {
    setSelectedTables((prev) => {
      const base = prev ?? new Set(entities.map((e) => e.name));
      const next = new Set(base);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const filteredDropdownEntities = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return entities;
    return entities.filter((e) => e.name.toLowerCase().includes(q));
  }, [entities, tableSearch]);

  const visibleEntities = useMemo(
    () => entities.filter((e) => isTableVisible(e.name)),
    [entities, isTableVisible],
  );

  const visibleRelationshipPairs = useMemo(() => {
    if (!showRelationships) return [];
    return relationships
      .map((rel, index) => ({ rel, index }))
      .filter(({ rel }) => {
        const f = relFields(rel);
        return f.from && f.to && isTableVisible(f.from) && isTableVisible(f.to);
      });
  }, [relationships, showRelationships, isTableVisible]);

  const layoutFallback = useMemo(
    () =>
      layoutMode === "star"
        ? starLayoutNodes(visibleEntities, relationships)
        : gridLayoutNodes(visibleEntities),
    [visibleEntities, relationships, layoutMode],
  );
  const [nodes, setNodes] = useState<Node[]>(layoutFallback);
  const edges = useMemo(
    () => buildEdges(visibleRelationshipPairs),
    [visibleRelationshipPairs],
  );

  // Re-sync nodes when the visible entity set changes, preserving any manual
  // drag positions for tables already shown — except right after the layout
  // mode itself changes, when we want the fresh auto-computed positions to
  // actually take effect instead of being overridden by stale drag state.
  const [lastLayoutMode, setLastLayoutMode] = useState(layoutMode);
  useEffect(() => {
    const layoutJustChanged = lastLayoutMode !== layoutMode;
    setNodes((prev) => {
      const prevPos = new Map(prev.map((n) => [n.id, n.position]));
      return layoutFallback.map((n) => ({
        ...n,
        position: layoutJustChanged
          ? n.position
          : (prevPos.get(n.id) ?? n.position),
      }));
    });
    if (layoutJustChanged) setLastLayoutMode(layoutMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutFallback, layoutMode]);

  const onNodesChange = useCallback((changes: any[]) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  const onNodeClick = useCallback(
    (_event: unknown, node: Node) => {
      onEditEntity?.(node.id);
    },
    [onEditEntity],
  );

  const onEdgeClick = useCallback((_event: unknown, edge: Edge) => {
    const index = (edge.data as { index?: number } | undefined)?.index;
    if (typeof index !== "number") return;
    setSelectedEdgeIndex(index);
    setEdgeError(null);
  }, []);

  const onConnect = useCallback(
    (params: Connection) => {
      setConnectError(null);
      const { source, target, sourceHandle, targetHandle } = params;
      if (!source || !target || !sourceHandle || !targetHandle) return;
      if (
        sourceHandle === ENTITY_HANDLE_ID ||
        targetHandle === ENTITY_HANDLE_ID
      ) {
        return;
      }
      if (!onCreateRelationship) return;
      const fromColumn = sourceHandle.replace(/^col:/, "");
      const toColumn = targetHandle.replace(/^col:/, "");
      onCreateRelationship(source, fromColumn, target, toColumn).catch(
        (err) => {
          setConnectError(
            err instanceof Error
              ? err.message
              : "Failed to create relationship",
          );
        },
      );
    },
    [onCreateRelationship],
  );

  useEffect(() => {
    if (selectedEdgeIndex === null) return;
    const rel = relationships[selectedEdgeIndex];
    setEdgeCardinalityDraft(rel ? relFields(rel).cardinality : "many_to_one");
  }, [selectedEdgeIndex, relationships]);

  const saveCardinality = async () => {
    if (selectedEdgeIndex === null || !onUpdateRelationshipCardinality) return;
    setEdgeBusy(true);
    setEdgeError(null);
    try {
      await onUpdateRelationshipCardinality(
        selectedEdgeIndex,
        edgeCardinalityDraft,
      );
      setSelectedEdgeIndex(null);
    } catch (err) {
      setEdgeError(
        err instanceof Error ? err.message : "Failed to update relationship",
      );
    } finally {
      setEdgeBusy(false);
    }
  };

  const deleteSelectedRelationship = async () => {
    if (selectedEdgeIndex === null || !onDeleteRelationship) return;
    setEdgeBusy(true);
    setEdgeError(null);
    try {
      await onDeleteRelationship(selectedEdgeIndex);
      setSelectedEdgeIndex(null);
    } catch (err) {
      setEdgeError(
        err instanceof Error ? err.message : "Failed to delete relationship",
      );
    } finally {
      setEdgeBusy(false);
    }
  };

  if (entities.length === 0) {
    return (
      <p className="text-xs text-white/30 italic">
        No entities to display yet.
      </p>
    );
  }

  const selectedRel =
    selectedEdgeIndex !== null ? relationships[selectedEdgeIndex] : undefined;
  const selectedRelFields = selectedRel ? relFields(selectedRel) : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <button
            type="button"
            onClick={() => setFilterOpen((o) => !o)}
            className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md bg-surface-200 hover:bg-surface-300 text-white/60 hover:text-white transition"
          >
            <Filter size={11} />
            Tables:{" "}
            {selectedTables === null
              ? `All (${entities.length})`
              : `${selectedTables.size}/${entities.length}`}
            <ChevronDown size={11} />
          </button>
          {filterOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setFilterOpen(false)}
              />
              <div className="absolute z-20 mt-1 w-60 max-h-80 overflow-y-auto custom-scrollbar rounded-lg border border-white/10 bg-surface-100 shadow-xl p-2">
                <div className="relative mb-1.5">
                  <Search
                    size={11}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-white/30"
                  />
                  <input
                    type="text"
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                    placeholder="Search tables…"
                    className="w-full pl-6 pr-2 py-1 text-[11px] bg-surface-300 border border-surface-200 rounded text-white placeholder:text-white/25"
                  />
                </div>
                <div className="flex items-center justify-between px-1 pb-1.5 mb-1 border-b border-white/10">
                  <button
                    type="button"
                    onClick={() => setSelectedTables(null)}
                    className="text-[10px] text-brand-400 hover:text-brand-300"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedTables(new Set())}
                    className="text-[10px] text-white/30 hover:text-white/60"
                  >
                    Clear
                  </button>
                </div>
                {filteredDropdownEntities.length === 0 ? (
                  <p className="px-1 py-2 text-[10px] text-white/30 italic">
                    No tables match "{tableSearch}".
                  </p>
                ) : (
                  filteredDropdownEntities.map((e) => (
                    <label
                      key={e.name}
                      className="flex items-center gap-1.5 px-1 py-1 text-[11px] text-white/70 hover:bg-white/5 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={isTableVisible(e.name)}
                        onChange={() => toggleTable(e.name)}
                        className="accent-brand-500"
                      />
                      <span className="truncate">{e.name}</span>
                    </label>
                  ))
                )}
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowRelationships((s) => !s)}
          className={`flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md transition ${
            showRelationships
              ? "bg-brand-500/15 text-brand-300 border border-brand-500/20"
              : "bg-surface-200 text-white/40 hover:text-white/60"
          }`}
        >
          {showRelationships ? <Eye size={11} /> : <EyeOff size={11} />}
          Linkages{" "}
          {showRelationships
            ? `(${visibleRelationshipPairs.length})`
            : "hidden"}
        </button>
        <div className="flex items-center rounded-md bg-surface-200 p-0.5">
          <button
            type="button"
            onClick={() => setLayoutMode("grid")}
            title="Grid layout"
            className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded transition ${
              layoutMode === "grid"
                ? "bg-brand-500/20 text-brand-300"
                : "text-white/40 hover:text-white/60"
            }`}
          >
            <LayoutGrid size={11} />
            Grid
          </button>
          <button
            type="button"
            onClick={() => setLayoutMode("star")}
            title="Star schema layout (facts centered, dimensions ringed)"
            className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded transition ${
              layoutMode === "star"
                ? "bg-brand-500/20 text-brand-300"
                : "text-white/40 hover:text-white/60"
            }`}
          >
            <Sparkles size={11} />
            Star
          </button>
        </div>
        <p className="text-[10px] text-white/25 ml-auto hidden sm:block">
          Click a table to edit it · click a linkage to change or remove it ·
          drag between column dots to propose a new one
        </p>
      </div>

      {connectError && (
        <p className="text-[10px] text-red-400">{connectError}</p>
      )}

      {visibleEntities.length === 0 ? (
        // Match the canvas's height so the enclosing collapsible section
        // (which clips overflow for its rounded corners) doesn't shrink and
        // cut off the still-open "Tables" filter dropdown above.
        <div
          style={{ height: 520 }}
          className="flex items-center justify-center rounded-lg border border-white/10 bg-surface-200/40"
        >
          <p className="text-xs text-white/30 italic px-1">
            No tables selected — choose at least one from the "Tables" filter
            above.
          </p>
        </div>
      ) : (
        <div
          style={{ height: 520 }}
          className="relative rounded-lg border border-white/10 bg-surface-200/40"
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            fitView
          >
            <Background gap={24} color="rgba(255,255,255,0.06)" />
            <Controls showInteractive={false} />
            <MiniMap
              pannable
              zoomable
              maskColor="rgba(10,10,15,0.6)"
              nodeColor="rgba(134,188,37,0.4)"
            />
          </ReactFlow>

          {selectedRelFields && (
            <div className="absolute top-3 right-3 z-20 w-64 rounded-lg border border-white/10 bg-surface-100 shadow-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-semibold text-white/80">
                  Edit linkage
                </p>
                <button
                  type="button"
                  onClick={() => setSelectedEdgeIndex(null)}
                  className="text-white/30 hover:text-white"
                >
                  <X size={12} />
                </button>
              </div>
              <p className="text-[10px] text-white/40 mb-2 break-all">
                {selectedRelFields.from}.{selectedRelFields.fromCol} →{" "}
                {selectedRelFields.to}.{selectedRelFields.toCol}
              </p>
              {typeof selectedRelFields.confidence === "number" && (
                <p className="text-[10px] text-white/30 mb-2">
                  Confidence: {Math.round(selectedRelFields.confidence * 100)}%
                </p>
              )}
              {edgeError && (
                <p className="text-[10px] text-red-400 mb-2">{edgeError}</p>
              )}
              <select
                value={edgeCardinalityDraft}
                onChange={(e) => setEdgeCardinalityDraft(e.target.value)}
                className="w-full mb-2 px-2 py-1 bg-surface-300 border border-surface-200 rounded text-[11px] text-white"
              >
                <option value="one_to_one">1:1</option>
                <option value="one_to_many">1:N</option>
                <option value="many_to_one">N:1</option>
                <option value="many_to_many">N:N</option>
              </select>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={saveCardinality}
                  disabled={edgeBusy}
                  className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white transition"
                >
                  {edgeBusy ? (
                    <Loader2 size={10} className="animate-spin" />
                  ) : (
                    <Check size={10} />
                  )}
                  Save
                </button>
                <button
                  type="button"
                  onClick={deleteSelectedRelationship}
                  disabled={edgeBusy}
                  className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-red-500/15 hover:bg-red-500/25 text-red-400 disabled:opacity-60 transition ml-auto"
                >
                  <Trash2 size={10} /> Delete
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
